import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, throwError, of, from, timer, EMPTY, BehaviorSubject } from 'rxjs';
import { catchError, mergeMap, retryWhen, switchMap, take, tap, filter } from 'rxjs/operators';
import { AuthenticateserviceService } from './_service/authenticateservice.service';
import { CustomControlsService } from './_service/custom-controls.service';
import { WhiteLabelService } from './_service/white-label.service';
import { SentryPerformanceService } from './_service/sentry-performance.service';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  private channelIdCache: string | null = null;

  // Single-flight refresh: when the access token expires, many concurrent
  // requests 401 at once (the home page alone fires several). Without a lock
  // each would call userLogin independently — and the 2nd+ racing refreshes
  // (same idToken) fail, hit catchError → logout() → device kicked. So only ONE
  // refresh runs; the rest wait on refreshedToken$ and retry with the new token.
  private isRefreshing = false;
  private refreshedToken$ = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthenticateserviceService,
    private toastrService: CustomControlsService,
    private wl: WhiteLabelService,
    private sentryPerf: SentryPerformanceService,
  ) {}

  /** Treat the literal strings "null"/"undefined"/"" as no token — localStorage
      stores everything as a string, so a botched setItem(token, undefined) lands
      as the string "undefined" and was being sent as `Authorization: Bearer null`. */
  private validToken(t: string | null): string | null {
    return t && t !== 'null' && t !== 'undefined' ? t : null;
  }

  /** True only for a backend block / identity kill — a 403/409 whose body says
      the user is blocked/suspended/deactivated (e.g. { message: "User is
      blocked!!" }). This is the ONE case a 403/409 should end the session; a
      plain resource 403/409 (forbidden item, undeployed route) must not. */
  private isBlocked(error: HttpErrorResponse): boolean {
    const msg = String(error?.error?.message || '').toLowerCase();
    return /block|suspend|deactivat/.test(msg);
  }

  /** Clone a request with a fresh Bearer token (+ the white-label header). */
  private withAuth(request: HttpRequest<any>, token: string): HttpRequest<any> {
    let headers = request.headers
      .set('Authorization', `Bearer ${token}`)
      .set('Cache-Control', 'no-cache');
    if (this.wl.isWhiteLabel() && this.wl.currentChannelId()) {
      headers = headers.set('Toqqer-Headers', JSON.stringify({ channelId: this.wl.currentChannelId() }));
    }
    return request.clone({ headers });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.includes('/logout') || req.url.startsWith('assets/') || req.url.includes('/assets/') || req.url.includes('/channels/getChannelId') || req.url.includes('/cms/MasterDataController/getGenre') || req.url.includes('/toqqerauth/guesttoken')) {
      return next.handle(req);
    }
    // Bearer selection: a logged-in user (has refreshToken/idToken) must NEVER
    // send the guest token — even in the brief window where the access `token`
    // was removed for a refresh. Sending the guest token there demotes the real
    // user to a guest. Guest token is only for genuinely anonymous visitors
    // (no token AND no refreshToken); when access is missing but a refreshToken
    // exists, send nothing → the 401 triggers a proper refresh.
    const accessToken = this.validToken(localStorage.getItem('token'));
    const loggedIn = !!this.validToken(localStorage.getItem('refreshToken'));
    const token = accessToken || (loggedIn ? null : this.validToken(localStorage.getItem('guestToken')));
    return from(
      Promise.race([
        this.wl.waitUntilReady(),
        new Promise(resolve => setTimeout(resolve, 2000))
      ])
    ).pipe(
      switchMap(() => {
        const isWL = this.wl.isWhiteLabel();
        const channelId = this.channelIdCache ?? this.wl.currentChannelId();
        if (!this.channelIdCache && channelId) {
          this.channelIdCache = channelId;
        }
        let headers = req.headers.set('Cache-Control', 'no-cache');
        if (token) {
          headers = headers.set('Authorization', `Bearer ${token}`);
        }
        if (isWL && channelId) {
          headers = headers.set('Toqqer-Headers', JSON.stringify({ channelId }));
        }
        const startTime = performance.now();
        return next.handle(req.clone({ headers })).pipe(
          tap(event => {
            if (event instanceof HttpResponse) {
              this.sentryPerf.trackApiCall(req.method, req.url, performance.now() - startTime, event.status);
            }
          })
        );
      }),
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error, retryCount) => {
            if (req.url.includes('/login') || req.url.includes('/refresh')) {
              return throwError(error);
            }
            if (error instanceof HttpErrorResponse && error.status === 0 && retryCount < 2) {
              return timer(800 * (retryCount + 1));
            }
            return throwError(error);
          })
        )
      ),
      catchError((err: HttpErrorResponse) =>
        this.handleHttpError(err, req, next)
      )
    );
  }

  private handleHttpError(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.sentryPerf.trackApiCall(request.method, request.url, 0, error.status);
    if (error.status === 401) {
      // If guest user's token expired, redirect to login
      if (this.authService.isGuest()) {
        localStorage.removeItem('guestToken');
        this.authService.logout();
        return EMPTY;
      }
      localStorage.removeItem('token');
      const refreshToken = this.validToken(localStorage.getItem('refreshToken'));
      if (refreshToken) {
        // A refresh is already running → wait for it and retry with the new
        // token, instead of firing a second (racing) userLogin that would fail.
        if (this.isRefreshing) {
          return this.refreshedToken$.pipe(
            filter(t => t != null),
            take(1),
            switchMap(t => next.handle(this.withAuth(request, t as string)))
          );
        }
        this.isRefreshing = true;
        this.refreshedToken$.next(null);
        return this.authService.toqqerUserLogin(refreshToken).pipe(
          // Catch ONLY a refresh failure here — a dead refresh token means the
          // session is over → logout. This MUST sit before the switchMap, so the
          // RETRIED request's own errors (e.g. an undeployed endpoint's 404) do
          // NOT land here and trigger a spurious logout.
          catchError(err => {
            this.isRefreshing = false;
            this.authService.logout();
            return throwError(err);
          }),
          switchMap((result: any) => {
            // Refresh succeeded but carried no usable token → also session-dead.
            if (!this.validToken(result?.refreshToken)) {
              this.isRefreshing = false;
              this.authService.logout();
              return throwError(new Error('refresh returned no token'));
            }
            localStorage.setItem('token', result.refreshToken);
            this.isRefreshing = false;
            this.refreshedToken$.next(result.refreshToken); // release the waiters
            // Retry the original request. Its errors propagate to the ORIGINAL
            // caller's catchError — they must NEVER reach the logout above.
            return next.handle(this.withAuth(request, result.refreshToken));
          })
        );
      }
      this.authService.logout();
      return EMPTY;
    }
    // 403/409: only a genuine block/identity kill ends the session. A plain
    // resource 403/409 (forbidden item, undeployed feature route) must NOT log
    // the user out — notify and propagate so the component handles it.
    if (error.status === 403 || error.status === 409) {
      if (this.isBlocked(error)) {
        this.toastrService.showNotification('error', '', error.error?.message || 'Your account has been blocked', 5000);
        this.authService.logout();
        return EMPTY;
      }
      const msg = error.error?.message || (error.status === 403 ? 'Access denied' : 'Conflict occurred');
      this.toastrService.showNotification(error.status === 403 ? 'error' : 'warning', '', msg, 5000);
      return throwError(error);
    }
    if (error.status === 0) {
      if (!navigator.onLine) {
        this.toastrService.showNotification('error', '', 'You are offline', 4000);
      } else {
        this.toastrService.showNotification('warning', '', 'Network issue. Please try again.', 3000);
      }
      return EMPTY;
    }
    return throwError(error);
  }
}

// @Injectable()
// export class HttpConfigInterceptor implements HttpInterceptor {
//   private channelIdCache: string | null = null;

//   constructor(
//     private authService: AuthenticateserviceService,
//     private toastrService: CustomControlsService,
//     private wl: WhiteLabelService,
//   ) {}

//   intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
//     if (req.url.includes('/logout')) {
//       return next.handle(req);
//     }
//     if (req.url.startsWith('assets/') || req.url.includes('/assets/')) {
//       return next.handle(req);
//     }
//     if (req.url.includes('/channels/getChannelId')) {
//       return next.handle(req);
//     }
//     const token = localStorage.getItem('token');
//     return from(
//       Promise.race([
//         this.wl.waitUntilReady(),
//         new Promise(resolve => setTimeout(resolve, 2000))
//       ])
//     )
//     .pipe(
//       switchMap(() => {
//         const isWL = this.wl.isWhiteLabel();
//         const channelId = this.channelIdCache ?? this.wl.currentChannelId();
//         if (!this.channelIdCache && channelId) {
//           this.channelIdCache = channelId;
//         }
//         let headers = req.headers.set('Cache-Control', 'no-cache');
//         if (token) headers = headers.set('Authorization', `Bearer ${token}`);
//         if (isWL && channelId) {
//           headers = headers.set('Toqqer-Headers', JSON.stringify({ channelId }));
//         }
//         const modified = req.clone({ headers });
//         return next.handle(modified);
//       }),
//       catchError((err: HttpErrorResponse) => this.handleHttpError(err, req, next))
//     );
//   }  

//   private handleHttpError(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
//     if (error.status === 401) {
//       localStorage.removeItem('token');
//       const refreshToken = localStorage.getItem('refreshToken');
//       if (refreshToken) {
//         return this.authService.toqqerUserLogin(refreshToken).pipe(
//           switchMap((result: any) => {
//             localStorage.setItem('token', result.refreshToken);
//             let headers = request.headers.set('Authorization', 'Bearer ' + result.refreshToken);
//             headers = headers.set('Cache-Control', 'no-cache');
//             const isWL = this.wl.isWhiteLabel();
//             const channelId = this.wl.currentChannelId();
//             if (isWL && channelId) {
//               headers = headers.set('Toqqer-Headers', JSON.stringify({ channelId }));
//             }
//             const newRequest = request.clone({ headers });
//             return next.handle(newRequest);
//           }),
//           catchError((refreshError) => {
//             this.handleError(refreshError);
//             localStorage.removeItem('refreshToken');
//             return throwError(refreshError);
//           })
//         );
//       } else {
//         this.openVerifyPhoneModal();
//       }
//     } else if (error.status === 403) {
//       this.handleBlock(error);
//     } else if (error.status === 409) {
//       this.authService.logout();
//       this.openVerifyPhoneModal();
//       this.showWarning(error.error?.message || 'Conflict occurred');
//     }else if (error.status === 0) {
//       if (!navigator.onLine) {
//         this.showError('You are offline');
//       } else {
//         this.showWarning('Network issue. Retrying…');
//       }
//     }    
//     return throwError(error);
//   }

//   private handleError(error: any): void {
//     this.authService.logout();
//     this.showWarning('Your session has expired. Please login again.');
//   }

//   private handleBlock(error: any): void {
//     const token = localStorage.getItem('token');
//     if (token) {
//       this.authService.logout();
//     }
//     console.log(error)
//     this.showError(error.error.message);
//   }

//   private openVerifyPhoneModal(): void {
//     this.authService.logout();
//   }

//   showError(message: any) {
//     this.toastrService.showNotification('error', '', message, 5000);
//   }

//   showWarning(message: any) {
//     this.toastrService.showNotification('warning', '', message, 5000);
//   }
// }




// private getChannelId(wlChannel: string): Observable<string | null> {
//   const apiUrl = `${URLS.getChannelId}/${encodeURIComponent(wlChannel)}`; 
//   return this.http.get<ChannelIdResponse>(apiUrl).pipe(
//     tap((response) => {
//       this.channelIdCache = response?.data?.channelId || null;
//       if (this.channelIdCache) {
//         localStorage.setItem('wl_content_provider_id', this.channelIdCache);
//       } else {
//         localStorage.removeItem('wl_content_provider_id');
//       }
//     }),
//     switchMap((response) => of(response?.data?.channelId || null)), 
//     catchError((error) => {
//       console.error('ChannelId API error:', error); 
//       return of(null);
//     })
//   );
// }

// private handleRequestWithChannelId(
//   request: HttpRequest<any>,
//   next: HttpHandler,
//   token: string | null,
//   channelId: string | null
// ): Observable<HttpEvent<any>> {
//   let headers = request.headers;
//   // headers = headers.set('Cache-Control', 'no-cache');
//   if (token) {
//     headers = headers.set('Authorization', 'Bearer ' + token);
//   }
//   if (channelId) {
//     headers = headers.set('Toqqer-Headers', JSON.stringify({ channelId }));
//   }
//   const modifiedRequest = request.clone({ headers });
//   return next.handle(modifiedRequest).pipe(
//     catchError((error: HttpErrorResponse) => {
//       return this.handleHttpError(error, request, next);
//     })
//   );
// }